export const DESIGNER_TERMS_VERSION = '1.0';
export const DESIGNER_TERMS_EFFECTIVE_DATE = '9/19/2025';

export const DESIGNER_TERMS_HTML = (
  <>
    <h1>IMObituary, LLC / DESIGNER LICENSING AGREEMENT</h1>

    <p>This Designer Licensing Agreement ("Agreement") is entered into by and between: IMObituary, LLC, a Colorado limited liability company ("IMObituary," "Company," "Licensee"); and Designer / Business ("Designer," "you," or "Licensor"). IMObituary and Designer may be referred to individually as a "Party" and collectively as the "Parties."</p>

    <h2>1. Purpose</h2>
    <p>Designer desires to submit original artwork for use on sympathy greeting cards offered for sale through the IMObituary online card store. This Agreement defines the terms under which IMObituary may license, reproduce, market, and sell cards incorporating Designer's artwork, and the compensation payable to Designer for such sales.</p>

    <h2>2. Ownership and Intellectual Property</h2>
    
    <h3>2.1 Retention of Rights</h3>
    <p>Designer retains all right, title, and interest, including all copyrights and intellectual property rights, in and to the submitted artwork ("Artwork"). No ownership rights are transferred under this Agreement.</p>
    
    <h3>2.2 License Grant</h3>
    <p>Designer grants IMObituary a non-exclusive, worldwide, revocable, royalty-bearing license during the term of this Agreement to:</p>
    <ul>
      <li>Display Artwork in the IMObituary Card Store;</li>
      <li>Reproduce Artwork solely for print-on-demand sympathy cards;</li>
      <li>Sell sympathy cards incorporating the Artwork;</li>
      <li>Transmit Artwork to third-party printing and mailing partners solely for order fulfillment;</li>
      <li>Use Artwork in marketing and promotional materials directly related to the sale of IMObituary cards.</li>
    </ul>

    <h3>2.3 Limitations</h3>
    <p>IMObituary shall not sell, sublicense, or license Artwork independently of the card products, nor use Artwork for unrelated commercial purposes.</p>
    
    <h3>2.4 Templates</h3>
    <p>IMObituary retains ownership of final card layouts, templates, formatting, and production files, excluding the underlying Artwork.</p>
    
    <h3>2.5 Marketing Use</h3>
    <p>Marketing and promotional use of Artwork is royalty-free. Designer commissions apply only to completed card sales.</p>

    <h2>3. Account Registration and Responsibilities</h2>
    
    <h3>3.1 Account Requirement</h3>
    <p>Designer must create, verify, and maintain an active IMObituary designer account.</p>
    
    <h3>3.2 Accuracy of Information</h3>
    <p>Designer agrees to provide accurate, current contact and business information within their IMObituary designer account and to promptly update such information as needed.</p>
    
    <h3>3.3 Account Security</h3>
    <p>Designer is solely responsible for maintaining the confidentiality of login credentials and for all activity occurring under their account. IMObituary is not responsible for unauthorized access caused by Designer's failure to safeguard credentials.</p>

    <h2>4. Payments, Commissions, and Stripe Connect</h2>
    
    <h3>4.1 Stripe Connect Required</h3>
    <p>Designer must successfully complete and verify Stripe Connect onboarding to receive commission payments. No commissions accrue or are payable until Stripe verification is complete.</p>
    
    <h3>4.2 Commission Rate</h3>
    <p>Designer shall receive a commission equal to ten percent (10%) of the retail price of each card sold.</p>

    <h3>4.3 Retail Price Defined</h3>
    <p>The "retail price" means the actual price paid by the customer for the card product itself at checkout, excluding:</p>
    <ul>
      <li>Sales or use taxes;</li>
      <li>Shipping or delivery charges;</li>
      <li>Payment processing fees;</li>
      <li>Discounts, coupons, promotional reductions, loyalty credits, or refunds.</li>
    </ul>
    
    <h3>4.4 Adjustments</h3>
    <p>Commissions are subject to adjustment for refunds, cancellations, chargebacks, or fraudulent transactions.</p>
    
    <h3>4.5 Payouts</h3>
    <p>Payouts are issued through Stripe according to Designer's Stripe payout preferences. IMObituary is not responsible for Stripe delays, holds, errors, or failed transfers.</p>
    
    <h3>4.6 Unpaid Earned Commissions</h3>
    <p>Any commissions that are earned but remain unpaid for any reason, including upon termination of this Agreement, will be paid in accordance with this Agreement.</p>
    
    <h3>4.7 Embossed or Foil Detailing Reimbursement</h3>
    <p>If the Designer elects to use embossed or foil detailing, the Designer will pay $0.65 per card upfront, with a minimum production quantity of 500 cards. This upfront payment will be reimbursed to the Designer only after the corresponding card is sold, and it shall be separate from and not included in commission payments.</p>

    <h2>5. Card Design Requirements and Submission Standards</h2>
    
    <h3>5.1 Design Specifications</h3>
    <p>All Artwork submitted must comply with IMObituary's technical specifications, which currently include, but are not limited to:</p>
    <ul>
      <li>Finished size: 5 x 7 inches folded (10 x 7 inches flat);</li>
      <li>Resolution: 300–400 DPI;</li>
      <li>Bleed: 1/8 inch on three sides;</li>
      <li>File format: All uploads must be submitted as PNG files;</li>
      <li>Orientation: All card designs must be submitted in portrait orientation. Landscape-style designs are not currently accepted and will be considered in the future.</li>
    </ul>
    
    <p><strong>Image File Requirements</strong></p>
    <p>Designer shall provide two (2) PNG image files for each approved design, as follows:</p>
    <ul>
      <li><strong>Print File</strong><br/>A high-resolution PNG file intended for production printing, containing all design elements as finalized for print.</li>
      <li><strong>Display / Mockup File</strong><br/>A PNG file suitable for digital display and mockups across a variety of print stock and background colors, in which all white areas are removed and rendered as transparent (i.e., white knocked out to transparency). The Display / Mockup File must preserve the integrity of all non-white design elements and be delivered with a transparent background.</li>
    </ul>

    <h3>5.2 Paper and Print Options</h3>
    <p>Approved paper stocks may include 120-weight accent opaque matte, 100-weight classic linen (Avalon bright white) and 130-weight Cougar Opaque Natural Cover. IMObituary may modify available paper options from time to time.</p>
    
    <h3>5.3 Content Standards</h3>
    <p>Artwork must be original, tasteful, appropriate for sympathy and memorial use, and must not include unlawful, defamatory, hateful, or infringing material.</p>
    
    <h3>5.4 Warranties</h3>
    <p>Designer represents and warrants that:</p>
    <ul>
      <li>The Artwork is original and owned or fully licensed by Designer;</li>
      <li>The Artwork does not infringe any copyright, trademark, moral right, or other intellectual property right;</li>
      <li>Designer has full authority to grant the license set forth in this Agreement.</li>
    </ul>
    
    <h3>5.5 Review and Approval</h3>
    <p>Submission of Artwork does not guarantee acceptance. IMObituary reserves the right to accept, reject, request revisions to, or remove Artwork at its sole discretion.</p>
    
    <h3>5.6 Specification Changes</h3>
    <p>IMObituary may update design, print, or formatting specifications with reasonable advance notice when material changes occur.</p>

    <h2>6. Removal of Designs and Termination</h2>
    
    <h3>6.1 Designer Removal</h3>
    <p>Designers may remove Artwork at any time through their account, provided that there are no pending orders in production or fulfillment. Any orders already in production will still be fulfilled even after the design is removed from the platform.</p>
    
    <h3>6.2 IMObituary Removal</h3>
    <p>IMObituary may remove or suspend Artwork at its discretion for quality concerns, policy violations, legal disputes, or catalog changes.</p>
    
    <h3>6.3 Termination</h3>
    <p>This Agreement remains in effect until terminated by either Party. IMObituary may terminate immediately for breach, unlawful content, or failure to maintain an active Stripe Connect account.</p>
    
    <h3>6.4 Effect of Termination</h3>
    <p>Upon termination:</p>
    <ul>
      <li>IMObituary's license to the Artwork ends, except as required to fulfill existing orders;</li>
      <li>Artwork will be removed from active sale;</li>
      <li>Earned but unpaid commissions will be paid in accordance with this Agreement.</li>
    </ul>
    
    <h3>6.5 Archival Copies</h3>
    <p>IMObituary may retain archived copies of Artwork solely for legal, accounting, or dispute-resolution purposes.</p>

    <h2>7. Independent Contractor Relationship</h2>
    <p>Designer is an independent contractor and licensor. Nothing in this Agreement creates an employment, partnership, joint venture, or agency relationship.</p>

    <h2>8. Data Privacy and Security</h2>
    
    <h3>8.1 Data Collection</h3>
    <p>IMObituary collects Designer information necessary for account administration, compliance, and order fulfillment. Payment information is handled exclusively by Stripe.</p>
    
    <h3>8.2 Data Protection</h3>
    <p>IMObituary does not sell Designer data and uses reasonable safeguards to protect account information, though absolute security cannot be guaranteed.</p>
    
    <h3>8.3 Designer Obligations</h3>
    <p>Designer agrees to use secure devices and promptly notify IMObituary of any unauthorized access.</p>

    <h2>9. Liability and Indemnification</h2>
    
    <h3>9.1 Limitation of Liability</h3>
    <p>To the fullest extent permitted by law, IMObituary shall not be liable for indirect, incidental, punitive, or consequential damages. IMObituary's total liability shall not exceed commissions paid to Designer in the six (6) months preceding the claim.</p>
    
    <h3>9.2 Indemnification</h3>
    <p>Designer agrees to indemnify and hold harmless IMObituary from any claims, damages, or expenses arising from infringement allegations, unlawful content, or breach of this Agreement.</p>
    
    <h3>9.3 Force Majeure</h3>
    <p>Neither Party shall be liable for delays or failures caused by events beyond reasonable control, including natural disasters, government actions, or service outages.</p>

    <h2>10. Unauthorized Use by Third Parties</h2>
    <p>Designer acknowledges that third parties may copy or misuse Artwork without authorization. IMObituary is not responsible for such actions but will reasonably cooperate if notified.</p>

    <h2>11. Governing Law and Venue</h2>
    <p>This Agreement shall be governed by and construed in accordance with the laws of the State of Colorado. Any legal action shall be brought exclusively in Colorado state or federal courts.</p>

    <h2>12. Amendments</h2>
    <p>IMObituary may update or modify this Agreement from time to time. Continued participation after notice of changes constitutes acceptance. Material changes will be communicated in advance.</p>

    <h2>13. Confidentiality</h2>
    <p>Designer agrees not to disclose IMObituary's proprietary or confidential information during or after the term of this Agreement.</p>

    <h2>14. Entire Agreement</h2>
    <p>This Agreement constitutes the entire agreement between the Parties and supersedes all prior agreements or understandings.</p>

    <h2>15. Survival</h2>
    <p>Sections concerning ownership, payment obligations, liability, indemnification, confidentiality, and governing law shall survive termination.</p>
  </>
);
